import { useState, useRef, useEffect } from "react";
import { X, Send } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SARAH_IMG = "/sarah-face.png";

function getSessionId() {
  let sid = sessionStorage.getItem("sarah_session");
  if (!sid) {
    sid = "sarah-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8);
    sessionStorage.setItem("sarah_session", sid);
  }
  return sid;
}

export default function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && !hasGreeted) {
      setMessages([{
        role: "assistant",
        content: "Hi! I'm Sarah, your storage and parking assistant. I can help you find spaces, compare cities, or plan a move anywhere in the Philippines. What can I help you with?"
      }]);
      setHasGreeted(true);
    }
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, hasGreeted]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: getSessionId(),
        message: userMsg,
        page_context: window.location.pathname
      });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Sorry, I'm having trouble connecting right now. You can always visit storajini.com directly to find storage or parking!"
      }]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat bubble — Sarah's face fills the entire circle, bigger size, strong pulse */}
      {!isOpen && (
        <button
          data-testid="chatbot-toggle-btn"
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-[10000] group cursor-pointer"
          style={{ width: 80, height: 80 }}
        >
          {/* Outer pulse ring */}
          <span className="absolute inset-[-6px] rounded-full bg-amber-400 opacity-40 animate-ping" />
          {/* Inner glow */}
          <span className="absolute inset-[-3px] rounded-full bg-amber-500 opacity-25 animate-pulse" />
          {/* Sarah face — fills entire bubble */}
          <div className="relative w-[80px] h-[80px] rounded-full overflow-hidden shadow-xl shadow-amber-500/40 group-hover:scale-110 transition-transform ring-[3px] ring-amber-500">
            <img src={SARAH_IMG} alt="Chat with Sarah" className="w-full h-full object-cover" />
          </div>
          {/* Online dot */}
          <span className="absolute bottom-0.5 right-0.5 w-4 h-4 bg-green-500 rounded-full border-[2.5px] border-white" />
        </button>
      )}

      {/* Chat window */}
      {isOpen && (
        <div data-testid="chatbot-window" className="fixed bottom-6 right-6 z-[10000] w-[370px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col animate-fade-in-up" style={{ height: "500px" }}>
          {/* Header with Sarah */}
          <div className="flex items-center justify-between px-4 py-3 bg-amber-500 rounded-t-2xl">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full overflow-hidden ring-2 ring-white/40 flex-shrink-0">
                <img src={SARAH_IMG} alt="Sarah" className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">Sarah</p>
                <p className="text-amber-100 text-xs flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-400 rounded-full inline-block" />
                  AI Storage Assistant
                </p>
              </div>
            </div>
            <button
              data-testid="chatbot-close-btn"
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Disclaimer */}
          <div className="px-3 py-1.5 bg-amber-50 text-xs text-amber-600 text-center border-b border-amber-100">
            Sarah is an AI assistant. For actual bookings, visit storajini.com
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} gap-2`}>
                {msg.role === "assistant" && (
                  <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 mt-1">
                    <img src={SARAH_IMG} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className={`max-w-[78%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.role === "user"
                    ? "bg-amber-500 text-white rounded-br-md"
                    : "bg-gray-100 text-gray-800 rounded-bl-md"
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start gap-2">
                <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 mt-1">
                  <img src={SARAH_IMG} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-md">
                  <div className="flex gap-1.5">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-3 py-3 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                data-testid="chatbot-input"
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask Sarah anything..."
                className="flex-1 px-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
              <button
                data-testid="chatbot-send-btn"
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="w-10 h-10 bg-amber-500 text-white rounded-full flex items-center justify-center hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
